import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { formatArrivalDate } from '../../../../../../utils/Functions';
import { ItineraryService } from '../../../../../../shared/itinerary.service';
import { RestService } from '../../../../../../shared/rest.service';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { ConfirmationModalComponent } from '../../../../../../shared/modals/confirmation-modal/confirmation-modal.component';
import { CurrencyConverterPurePipe } from 'app/shared/currency-converter-pure.pipe';
import { UserService } from 'app/pages/com.usblick.common/auth/user.service';
import { HttpErrorResponse } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { NotificationService } from 'app/shared/notifications/notification.service';

@Component({
  selector: 'usb-item-flight',
  templateUrl: './item-flight.component.html',
  styleUrls: ['./item-flight.component.scss'],
  providers: [CurrencyConverterPurePipe]
})
export class ItemFlightComponent implements OnInit {
  @Input() dato: any;
  @Input() isCheckIn: boolean;
  @Input() partialReserve: boolean;
  @Input() clientSetting: any;
  @Input() permissions: any;
  @Input() currencies: any;
  @Input() currencyAgency: any;
  @Output() itemToPartial = new EventEmitter<number>();
  @Output() itemDelete = new EventEmitter<any>();
  @Output() delete = new EventEmitter<string>();
  @Output() cancel = new EventEmitter<string>();

  public formatDate = formatArrivalDate;
  bsModalRef?: BsModalRef;

  constructor(
    private itineraryService: ItineraryService,
    private currencyPipe: CurrencyConverterPurePipe,
    private restService: RestService,
    public userService: UserService,
    private modalService: BsModalService,
    private ngxSpinner: NgxSpinnerService,
    private notificationService: NotificationService,
  ) { }

  ngOnInit(): void { console.log("AEREO: ", this.dato)}

  // El backend persiste arrivalDate/departureDate con la misma convención
  // que hoteles (arrival = fecha más temprana/ida, departure = más
  // tardía/vuelta) y sobrescribe el departureDate propio del producto — por
  // eso acá se usan esos dos campos y no item.departureDate/item.returnDate.
  isRoundTrip(): boolean {
    return !!this.dato?.arrivalDate && !!this.dato?.departureDate && this.dato.arrivalDate !== this.dato.departureDate;
  }

  // Vuelo elegido "Por tramos": cada tramo es su propio item, tageado por
  // FlyProductService con item.direction ('IDA'/'VUELTA'/'Tramo N').
  getDirectionLabel(): string {
    if (!this.dato?.direction) {
      return '';
    }
    if (this.dato.direction === 'IDA') { return 'Ida'; }
    if (this.dato.direction === 'VUELTA') { return 'Vuelta'; }
    return this.dato.direction;
  }

  getPrice(item: any): number {
    if (item.status_id === 5) {
      return 0;
    }

    let price: number;

    if (!item.isPendigApprovePay && !item.isPaidAgency && !item.isPaidProvider) {
      price = item.netPriceAgent ?? item.pricing?.totalPriceProf ?? item.totalPriceProf;
      price = this.currencyPipe.transform(
        price,
        item?.currency,
        this.currencyAgency?.code,
        this.currencies
      );
    }

    if (item.isPendigApprovePay || item.isPaidAgency || item.isPaidProvider) {
      if (item.converted_amounts) {
        price = item.converted_amounts.netPriceAgent;
      } else {
        price = item.netPriceAgent ?? item.pricing?.totalPriceProf ?? item.totalPriceProf;
      }
    }

    return price;
  }

  toPartialReserve(event, item: any): void {
    if (event.target.checked) {
      this.itineraryService.updateItineraryPartial(item);
      this.itemToPartial.emit(1);
    } else {
      this.itineraryService.updateItineraryPartial(item, true);
      this.itemToPartial.emit(-1);
    }
  }

  removeItem(item: any): void {
    let message = '';

    if (item.status_id === 1 || item.status_id === 5) {
      message = '¿Esta seguro de eliminar el producto?';
    }
    if (item.status_id === 2) {
      message = '¿Esta seguro de cancelar el producto?';
    }

    const initialState: ModalOptions = {
      initialState: {
        title: item.status_id === 2 ? 'Cancelar' : 'Eliminar',
        message: message.replace(/\?/g, '?<br />'),
        icon: 'assets/icons/itinerary-detail/04-01.svg'
      }
    };

    this.bsModalRef = this.modalService.show(ConfirmationModalComponent, initialState);
    const confirmSubscription = this.bsModalRef.content.confirmResult.subscribe((result: boolean) => {
      if (result) {
        if (item.status_id === 1 || item.status_id === 5) {
          this.deleteItem(item);
        }
        if (item.status_id === 2) {
          this.cancelReservation(item);
        }
      }
      confirmSubscription.unsubscribe();
    });
  }

  deleteItem(item: any): void {
    this.itineraryService.remove(item !== undefined ? item : item);
    setTimeout(() => {
      this.delete.emit(item.id);
      this.userService.updateProductsPrices(item.itinerary_id);
    }, 1000);
  }

  cancelReservation(item: any): void {
    this.ngxSpinner.show();

    const cancelData: any = {
      providerId: item?.providerId,
      reservationLocator: item?.resultReserve?.providerReservationId,
    };

    this.restService.doPut('app', `products/cancelReservation/${item?.itinerary_id}`, cancelData).subscribe({
      next: () => {
        this.ngxSpinner.hide();
        this.notificationService.success('Su producto fue cancelado con éxito');
        this.cancel.emit(item.id);
        this.itineraryService.updateItinerary(this.userService.itineraryActive[0], false);
      },
      error: (error: HttpErrorResponse) => {
        this.ngxSpinner.hide();
        this.notificationService.error('Ocurrió un error');
        console.log(error);
      }
    });
  }

  // ===== Helpers para el nuevo layout =====

  // Devuelve el optionId elegido dentro del leg correspondiente a este item.
  // selectedOptions guarda los optionIds en el orden de los legs; este item
  // es un tramo, así que usamos direction ('IDA'/'VUELTA') o fallback al
  // primer leg que coincida con origin/destination del item.
  private getSelectedOptionId(): string | undefined {
    const opts: string[] | undefined = this.dato?.selectedOptions;
    if (!opts || !opts.length) return undefined;

    const legs = this.dato?.itinerary ?? [];
    const direction = this.dato?.direction;

    let legIndex = 0;
    if (direction === 'VUELTA') {
      legIndex = legs.length > 1 ? 1 : 0;
    }

    return opts[legIndex] ?? opts[0];
  }

  // Devuelve el primer segmento del option elegido (un tramo = una opción).
  getFirstSegment(): any {
    const optionId = this.getSelectedOptionId();
    const legs = this.dato?.itinerary ?? [];
    if (!legs.length) return null;

    const direction = this.dato?.direction;
    const leg = direction === 'VUELTA' && legs.length > 1 ? legs[1] : legs[0];

    const option = leg?.options?.find((o: any) => o.optionId === optionId) ?? leg?.options?.[0];
    return option?.segments?.[0] ?? null;
  }

  // Devuelve la opción completa (necesitamos duration/stops del option, no del segmento).
  getSelectedOption(): any {
    const optionId = this.getSelectedOptionId();
    const legs = this.dato?.itinerary ?? [];
    if (!legs.length) return null;

    const direction = this.dato?.direction;
    const leg = direction === 'VUELTA' && legs.length > 1 ? legs[1] : legs[0];

    return leg?.options?.find((o: any) => o.optionId === optionId) ?? leg?.options?.[0] ?? null;
  }

  // "Directo" si stops === 0, "1 escala" si === 1, "N escalas" si > 1.
  getFlightType(): string {
    const option = this.getSelectedOption();
    const stops: number = option?.stops ?? 0;
    if (stops === 0) return 'Directo';
    if (stops === 1) return '1 escala';
    return `${stops} escalas`;
  }

  // 200 min -> "3h 20min", 60 min -> "1h"
  formatDuration(minutes: number | null | undefined): string {
    if (!minutes && minutes !== 0) return '';
    const m = Math.round(minutes);
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (h === 0) return `${r}min`;
    if (r === 0) return `${h}h`;
    return `${h}h ${r}min`;
  }

  // "2026-10-10T12:20:00" -> "12:20 hs." (sin conversión de timezone).
  formatTime(iso: string | null | undefined): string {
    if (!iso) return '';
    // Si viene con timezone (+00:00 / Z), respetarlo; si no, tratar como local.
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm} hs.`;
  }

  // Cabina legible: 'ECONOMY' -> 'Economy', 'PREMIUM_ECONOMY' -> 'Premium Economy'
  formatCabin(raw: string | null | undefined): string {
    if (!raw) return '';
    return raw
      .toLowerCase()
      .split('_')
      .map(p => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  }

  // Texto del chip según direction.
  getDirectionChipText(): string {
    const dir = this.dato?.direction;
    if (dir === 'IDA') return 'Ida';
    if (dir === 'VUELTA') return 'Vuelta';
    return dir ?? '';
  }
}
